import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { AlertTriangle, ArrowRight, XCircle, Upload, LifeBuoy } from 'lucide-react';

/**
 * Iter-205 / Iter-220p — Talent-facing alert banner for rejected IDV /
 * certifications.
 *
 * Mounted on:
 *   • /talent/home      — Iter-220p (the actual landing page after login)
 *   • /talent/dashboard — Iter-205   (older entry point)
 *   • /talent/profile-info — Iter-220p (so it follows the user)
 *
 * Polls the talent's profile + active certifications and surfaces a
 * loud "your X was rejected because Y" banner with a CTA that deep-links
 * into `/talent/idv?reupload=1` (the IDV page auto-scrolls to the upload
 * form when this query param is set).
 *
 * If multiple items are rejected, we stack them. The CTA is intentionally
 * oversized on mobile so it's impossible to miss — the prior compact pill
 * was getting lost in the page chrome.
 */

const RejectionBanner = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [profileRes, certsRes] = await Promise.all([
          api.get('/talent/profile').catch(() => null),
          api.get('/certifications/my-certifications').catch(() => null),
        ]);
        const out = [];
        const p = profileRes?.data || {};
        // IDV rejection
        if ((p.idv_status || '').toLowerCase() === 'rejected') {
          out.push({
            key: 'idv',
            title: 'Your ID verification needs new uploads',
            reason: p.idv_rejection_reason || p.id_rejection_reason || p.idv_reason || 'Please re-upload your ID.',
            notes: p.idv_admin_notes || null,
            link: '/talent/idv?reupload=1',
            cta: 'Re-upload ID Documents',
          });
        }
        // Cert rejections
        const certs = certsRes?.data?.certifications || certsRes?.data || [];
        if (Array.isArray(certs)) {
          for (const c of certs) {
            if ((c.status || '').toLowerCase() === 'rejected') {
              out.push({
                key: `cert-${c.id}`,
                title: `${c.type_name || c.certification_type} needs re-upload`,
                reason: c.rejection_reason || 'Please re-upload this certification.',
                notes: c.notes || null,
                link: '/talent/certifications',
                cta: 'Re-upload Certification',
              });
            }
          }
        }
        if (alive) setItems(out);
      } catch (_) {
        if (alive) setItems([]);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="talent-rejection-banner">
      {items.map((it) => (
        <div
          key={it.key}
          className="rounded-xl border-2 border-rose-300 bg-gradient-to-br from-rose-50 to-amber-50 shadow-sm overflow-hidden"
          data-testid={`talent-rejection-${it.key}`}
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-rose-900 text-base sm:text-lg flex items-center gap-2 flex-wrap">
                  <XCircle className="w-4 h-4 shrink-0" /> {it.title}
                </div>
                <div className="text-sm text-rose-800 mt-1.5">
                  <span className="font-semibold">Reason:</span> {it.reason}
                </div>
                {it.notes && (
                  <div className="text-sm text-rose-700 mt-1">
                    <span className="font-semibold">Reviewer note:</span> {it.notes}
                  </div>
                )}
                <p className="text-xs text-rose-700/80 mt-2">
                  Tap the button below to upload new documents. Review typically takes &lt; 24 hours.
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Link
                to={it.link}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm sm:text-base font-semibold shadow-sm transition-colors"
                data-testid={`talent-rejection-reupload-${it.key}`}
              >
                <Upload className="w-4 h-4" /> {it.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:support@ievntur.com?subject=IDV%20Help%20Needed"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg border border-rose-200 bg-white hover:bg-rose-50 text-rose-700 text-sm font-medium"
                data-testid={`talent-rejection-support-${it.key}`}
              >
                <LifeBuoy className="w-4 h-4" /> Support
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RejectionBanner;
